import React, { useState, useCallback } from "react"
import {
  faChevronDown,
  faChevronUp,
  faFileLines,
  faUpload,
  faCircleCheck,
  faFileCirclePlus,
  faDatabase,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button } from "antd"
import DataImport from "features/DataImport/components/DataImport.component"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import AddReportModal from "./AddReportModal.component"
import DataQButton from "shared/ui/Buttons/DataQButton.component"

import { useNavigate } from "react-router-dom"

import { useDataImport } from "features/DataImport/api/useDataImport"
import { useDashboard } from "features/Reporting/hooks/Dashboard/useDashboard"
import { useDeleteProjectMutation } from "features/Projects/api/useProjectsMutations"
import { notify } from "shared/ui/Notifications/notify"
import handleSequenceError from "utils/handleSequenceError"
import { Project } from "stores/projectStore"
import { DATAFILE_TYPE } from "types/dataImport.types"

// Props interface for the component
interface IntegratedReportSelectorProps {
  /** Callback function when a report is selected */
  onReportSelect: (report: string | null) => void
  /** Currently selected report name */
  selectedReport: string | null
  /** Whether data has been uploaded for the project */
  dataUploaded: boolean
  /** Array of available report names */
  availableReports: string[]
  /** Callback function to refresh data */
  onDataRefresh: () => void
  /** Project object containing project details */
  project: Project
  /** Callback function to refresh only the report list */
  handleRefreshReportListOnly?: () => Promise<void>
}

const IntegratedReportSelector: React.FC<IntegratedReportSelectorProps> = ({
  onReportSelect,
  selectedReport,
  dataUploaded,
  availableReports,
  onDataRefresh,
  project,
  handleRefreshReportListOnly,
}) => {
  const { deleteDataImportFile } = useDataImport()
  const { deleteDashboardFile } = useDashboard()
  const deleteProjectMutation = useDeleteProjectMutation()
  const navigate = useNavigate()

  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] =
    useState<boolean>(false)
  const [isDeleteDashboardModalVisible, setIsDeleteDashboardModalVisible] =
    useState<boolean>(false)
  const [reportToDelete, setReportToDelete] = useState<string | null>(null)

  const handleReportSelect = (report: string): void => {
    onReportSelect(report) // report is just a string name
    setIsExpanded(false) // Close dropdown after selection
  }

  const handleNewReportConfigSaved = useCallback(
    async (newReportName: string): Promise<void> => {
      if (handleRefreshReportListOnly) {
        await handleRefreshReportListOnly() // Update the list in ReportingContainer
      }
      onReportSelect(newReportName) // Select the new report
      setIsExpanded(false) // Close dropdown
    },
    [handleRefreshReportListOnly, onReportSelect, setIsExpanded]
  )

  const deleteProject = async (
    e: React.MouseEvent<HTMLElement>
  ): Promise<void> => {
    e.preventDefault()
    try {
      await deleteProjectMutation.mutateAsync(project.id)
      navigate("/home")
    } catch (error) {
      // Notifications are handled by the mutation; just log.
      console.error("Error deleting project:", error)
    }
  }

  const deleteFile = async (type: DATAFILE_TYPE): Promise<void> => {
    try {
      const res = await deleteDataImportFile(
        project.id,
        project.Created_by,
        type
      )
      if (handleSequenceError(res.data, "Error Deleting Data File!")) {
        notify.toast.success("Data File Deleted Successfully!")
        onDataRefresh()
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      notify.error("Error deleting data file!", errorMessage)
    }
  }

  const deleteReport = async (
    e: React.MouseEvent<HTMLElement>,
    reportName: string
  ): Promise<void> => {
    e.stopPropagation()
    try {
      const res = await deleteDashboardFile(
        project.id,
        project.Created_by,
        reportName
      )
      if (handleSequenceError(res.data, "Error Deleting Report!")) {
        notify.toast.success("Report Deleted Successfully!")
        if (handleRefreshReportListOnly) {
          await handleRefreshReportListOnly() // Refresh only the list
        }
        if (selectedReport === reportName) {
          onReportSelect(null) // Clear selection if the active report was deleted
        }
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error)
      notify.error("Error deleting report!", errorMessage)
    }
  }

  const handleDeleteDashboardModal = (
    e: React.MouseEvent<HTMLElement>
  ): void => {
    setIsDeleteDashboardModalVisible(false)
    const reportName = reportToDelete
    setReportToDelete(null) // Clear the stored report
    if (reportName) {
      deleteReport(e, reportName) // Use the stored report name
    }
  }

  const getButtonSubtitle = (): string => {
    if (!dataUploaded) return "Data Required"
    if (selectedReport) return "Dashboard Ready"
    if (availableReports.length === 0) return "No Dashboards"
    return "Data Ready"
  }

  const getButtonMainText = (): string => {
    if (!dataUploaded) return "Upload Project Data"
    if (selectedReport) return selectedReport // selectedReport is just a string
    if (availableReports.length === 0) return "Create First Dashboard"
    return "Choose a dashboard"
  }

  const getButtonIcon = () => {
    if (!dataUploaded) return faUpload
    if (selectedReport) return faFileLines
    return faDatabase
  }

  const getButtonIconColor = (): string => {
    if (!dataUploaded) return "text-gray-400"
    if (selectedReport) return "text-[#36c3ed]"
    return "text-green-500"
  }

  /**
   * Handles hiding the delete confirmation modal
   */
  const handleHideDeleteModal = (): void => {
    setIsDeleteModalVisible(false)
  }

  return (
    <>
      <ConfirmationModal
        title={"Delete Project"}
        isVisible={isDeleteModalVisible}
        cancel={handleHideDeleteModal}
        confirm={deleteProject}
        confirmButtonText="Delete"
        confirmButtonType="danger"
        message="Are you sure you want to delete this project? This action cannot be undone."
      />
      <ConfirmationModal
        title={"Delete Dashboard"}
        isVisible={isDeleteDashboardModalVisible}
        cancel={() => {
          setIsDeleteDashboardModalVisible(false)
          setReportToDelete(null) // Clear the stored report
        }}
        confirm={handleDeleteDashboardModal}
        confirmButtonText="Delete"
        confirmButtonType="danger"
        message="Are you sure you want to delete this dashboard? This action cannot be undone."
      />
      <div className="inline-block relative">
        {/* Dropdown Button */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`bg-white flex items-center gap-2 px-3 py-2 rounded-b-lg transition-colors w-[180px] xlplus:w-[250px] border border-gray-200 shadow-sm hover:border-gray-300 `}
        >
          <FontAwesomeIcon
            icon={getButtonIcon()}
            className={`text-lg ${getButtonIconColor()}`}
          />
          <div className="flex flex-col items-start min-w-0">
            <span className="text-xs text-gray-500 font-medium">
              {getButtonSubtitle()}
            </span>
            <span className="text-sm font-medium truncate w-full text-left">
              {getButtonMainText()}
            </span>
          </div>
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            className="text-sm ml-auto text-gray-400"
          />
        </button>

        {/* Dropdown Panel */}
        {isExpanded && (
          <div className="absolute z-50 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg w-[400px]">
            {!dataUploaded ? (
              // Section to Upload Project Data
              <>
                <div className="flex flex-row p-3 border-b border-gray-200">
                  <h3 className="text-base font-medium m-0 my-auto">
                    Upload Project Data
                  </h3>
                  <DataQButton
                    onClick={() => setIsDeleteModalVisible(true)}
                    type="primary"
                    isLoading={deleteProjectMutation.isPending}
                    className="ml-auto"
                    size="small"
                  >
                    Delete Project
                    <FontAwesomeIcon className="ml-2" icon={faTrash} />
                  </DataQButton>
                </div>
                <div className="p-4 text-center">
                  <FontAwesomeIcon
                    icon={faUpload}
                    className="text-3xl text-gray-400 mb-3"
                  />
                  <p className="text-sm text-gray-600 mb-4">
                    Upload a DQD data file to start creating dashboards for
                    this project.
                  </p>
                  <DataImport
                    name="Upload DQD Data"
                    refresh={onDataRefresh}
                    type={DATAFILE_TYPE.DQD}
                  />
                </div>
                <div className="p-3 border-t border-gray-200 bg-gray-50 text-right">
                  <span className="text-xs text-gray-600 italic">
                    Once uploaded, you can create and select dashboards.
                  </span>
                </div>
              </>
            ) : availableReports.length === 0 ? (
              // Section when data is uploaded but no reports exist
              <>
                <div className="p-3 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-base font-medium m-0 my-auto">
                    Dashboards
                  </h3>
                  <DataQButton
                    onClick={() => setIsDeleteModalVisible(true)}
                    type="primary"
                    isLoading={deleteProjectMutation.isPending}
                    className="ml-auto mr-2"
                    size="small"
                  >
                    Delete Project
                    <FontAwesomeIcon className="ml-2" icon={faTrash} />
                  </DataQButton>
                </div>

                <div className="p-2 bg-green-50 border-b border-green-200 text-xs">
                  <div className="flex items-center justify-between text-green-700">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
                      <span>Project data loaded successfully.</span>
                    </div>
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<FontAwesomeIcon icon={faTrash} />}
                      onClick={() => deleteFile(DATAFILE_TYPE.DQD)}
                      className="!text-xs !text-red-500 hover:!text-red-700"
                    >
                      Delete Data
                    </Button>
                  </div>
                </div>

                <div className="p-4 text-center">
                  <FontAwesomeIcon
                    icon={faFileCirclePlus}
                    className="text-3xl text-gray-400 mb-3"
                  />
                  <p className="text-sm text-gray-600 mb-4">
                    No dashboards have been created for this project yet. Create
                    your first dashboard to get started.
                  </p>
                  <AddReportModal
                    project={project}
                    onNewReportConfigSaved={handleNewReportConfigSaved}
                  />
                </div>

                <div className="p-3 border-t border-gray-200 bg-gray-50 text-right">
                  <span className="text-xs text-gray-600 italic">
                    Dashboards will be saved as configuration files tied to your
                    project data.
                  </span>
                </div>
              </>
            ) : (
              // Section to Select Reports (Data is uploaded and reports are available)
              <>
                <div className="p-3 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="text-base font-medium m-0 my-auto">
                    Dashboards
                  </h3>
                  <DataQButton
                    onClick={() => setIsDeleteModalVisible(true)}
                    type="primary"
                    isLoading={deleteProjectMutation.isPending}
                    className="ml-auto mr-2"
                    size="small"
                  >
                    Delete Project
                    <FontAwesomeIcon className="ml-2" icon={faTrash} />
                  </DataQButton>
                  <AddReportModal
                    project={project}
                    onNewReportConfigSaved={handleNewReportConfigSaved}
                  />
                </div>
                <div className="p-2 bg-green-50 border-b border-green-200 text-xs">
                  <div className="flex items-center justify-between text-green-700">
                    <div className="flex items-center">
                      <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
                      <span>
                        Project data loaded. {availableReports.length} report
                        {availableReports.length !== 1 ? "s" : ""} available.
                      </span>
                    </div>
                    <Button
                      type="link"
                      size="small"
                      danger
                      icon={<FontAwesomeIcon icon={faTrash} />}
                      onClick={() => deleteFile(DATAFILE_TYPE.DQD)}
                      className="!text-xs !text-red-500 hover:!text-red-700"
                    >
                      Delete Data
                    </Button>
                  </div>
                </div>

                <div className="max-h-[350px] overflow-y-auto p-2">
                  {availableReports.map((reportName: string, index: number) => (
                    <div
                      key={reportName || index}
                      onClick={() => handleReportSelect(reportName)}
                      className={`mb-2 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
                        selectedReport === reportName
                          ? "border-[#36C3ED] bg-blue-50"
                          : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-grow">
                          <div className="flex flex-row items-center">
                            <h3 className="text-base font-semibold text-gray-900 m-0">
                              {reportName}
                            </h3>
                            <span
                              onClick={(
                                e: React.MouseEvent<HTMLSpanElement>
                              ) => {
                                e.stopPropagation() // Prevent selecting the report
                                setReportToDelete(reportName)
                                setIsDeleteDashboardModalVisible(true)
                              }}
                              className="cursor-pointer ml-auto text-xs text-red-500 hover:text-red-700"
                            >
                              Delete
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="p-3 border-t border-gray-200 bg-gray-50 text-right">
                  <span className="text-xs text-gray-600 italic">
                    Select a dashboard to continue.
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default IntegratedReportSelector
